import React, { useEffect, useState, forwardRef } from "react";
import { Input, Select, Button, Form, Card, Collapse } from "antd";
import FeatherIcon from "feather-icons-react";
import { X, PlusCircle } from "react-feather";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getProductVariationsByProductId,
  createProductOrder,
  createShipment,
  createReceiving,
  uploadShipmentTrackingFile,
  uploadOrderFile,
  updateReceiving,
  updateShipment,
  getProductOrderById,
  updateProductOrder,
  deleteShipment,
  deleteShipmentTrackingFile,
  deleteOrderFile,
  createOrderItem,
  getProductsByBrand,
  deleteOrderItem,
  updateOrderItem,
  deleteReceiving,
} from "../../core/api/products";

import {
  getAllStatus,
  getSuppliersByType,
  getWarehouseAddresses,
  fetchBrandsBySelectedClients,
} from "../../core/api/settings";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { setSelectedClients } from "../../core/redux/action";

const { Option } = Select;

const OrderDetailsView = forwardRef(
  (
    {
      isVisible,
      onClose,
      orderId,
      setOrderId,
      fetchOrderList,
      onResize,
      clientId,
    },
    ref
  ) => {
    const data = useSelector((state) => state.toggle_header);
    const [brands, setBrands] = useState([]);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [existingFiles, setExistingFiles] = useState([]);
    const [newFiles, setNewFiles] = useState([]);
    const [removedFiles, setRemovedFiles] = useState([]);
    const [products, setProducts] = useState([]);
    const [productVariationsList, setProductVariationsList] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [creationStatus, setCreationStatus] = useState([]);
    const [orderSummary, setOrderSummary] = useState([]);
    const [orderForm] = Form.useForm();
    const [orderItems, setOrderItems] = useState([
      {
        productVariation: "",
        warehouse: "",
        quantity: 0,
        unitPrice: 0,
        shipments: [],
        receivings: [],
      },
    ]);
    const [orderItemForm] = Form.useForm();

    const MySwal = withReactContent(Swal);
    const dispatch = useDispatch();

    const addOrderItem = () => {
      setOrderItems([
        ...orderItems,
        {
          productVariation: "",
          warehouse: "",
          quantity: 0,
          unitPrice: 0,
          shipments: [],
          receivings: [],
        },
      ]);
    };

    const updateOrderItemFunction = (index, updatedItem) => {
      const updatedOrderItems = [...orderItems];
      updatedOrderItems[index] = updatedItem;
      setOrderItems(updatedOrderItems);
    };

    const deleteOrderItemFunction = async (index) => {
      try {
        const result = await Swal.fire({
          title: "Are you sure?",
          text: "This action will permanently delete the order item.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete it!",
          cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
          const updatedOrderItems = [...orderItems];
          const itemToDelete = updatedOrderItems[index];

          if (itemToDelete?.id) {
            await deleteOrderItem(itemToDelete.id);
            Swal.fire({
              title: "Deleted!",
              text: "The order item has been deleted from the database.",
              icon: "success",
              confirmButtonText: "OK",
            });
          } else {
            Swal.fire({
              title: "Deleted!",
              text: "The order item has been removed locally.",
              icon: "success",
              confirmButtonText: "OK",
            });
          }
          updatedOrderItems.splice(index, 1);
          setOrderItems(updatedOrderItems);
        }
      } catch (error) {
        console.error("Error deleting order item:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to delete the order item. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };

    const addShipmentToOrderItem = (index) => {
      const updatedOrderItems = [...orderItems];
      updatedOrderItems[index]?.shipments?.push({
        shipmentDate: "",
        quantity: 0,
        trackingNumber: "",
        newFiles: [],
        existingFiles: [],
      });
      setOrderItems(updatedOrderItems);
    };

    const addReceivingToOrderItem = (index) => {
      const updatedOrderItems = [...orderItems];
      updatedOrderItems[index].receivings.push({
        deliveryDate: "",
        quantity: 0,
      });
      setOrderItems(updatedOrderItems);
    };

    const updateShipmentFunction = (index, shipmentIndex, updatedShipment) => {
      const updatedOrderItems = [...orderItems];
      const updatedShipments = [...updatedOrderItems[index].shipments];

      updatedShipments[shipmentIndex] = {
        ...updatedShipments[shipmentIndex],
        ...updatedShipment,
      };

      updatedOrderItems[index] = {
        ...updatedOrderItems[index],
        shipments: updatedShipments,
      };

      setOrderItems(updatedOrderItems);
    };

    const updateReceivingFunction = (
      index,
      receivingIndex,
      updatedReceiving
    ) => {
      const updatedOrderItems = [...orderItems];
      const updatedReceivings = [...updatedOrderItems[index].receivings];

      updatedReceivings[receivingIndex] = {
        ...updatedReceivings[receivingIndex],
        ...updatedReceiving,
      };

      updatedOrderItems[index] = {
        ...updatedOrderItems[index],
        receivings: updatedReceivings,
      };

      setOrderItems(updatedOrderItems);
    };

    const handleTrackingFileUpload = (orderItemIndex, shipmentIndex, files) => {
      const updatedOrderItems = [...orderItems];
      if (!updatedOrderItems[orderItemIndex]) {
        console.error(`Order item at index ${orderItemIndex} does not exist.`);
        return;
      }
      if (!updatedOrderItems[orderItemIndex].shipments) {
        updatedOrderItems[orderItemIndex].shipments = [];
      }
      if (!updatedOrderItems[orderItemIndex].shipments[shipmentIndex]) {
        updatedOrderItems[orderItemIndex].shipments[shipmentIndex] = {
          newFiles: [],
        };
      }

      if (
        !updatedOrderItems[orderItemIndex].shipments[shipmentIndex].newFiles
      ) {
        updatedOrderItems[orderItemIndex].shipments[shipmentIndex].newFiles =
          [];
      }

      updatedOrderItems[orderItemIndex].shipments[shipmentIndex].newFiles = [
        ...updatedOrderItems[orderItemIndex].shipments[shipmentIndex].newFiles,
        ...Array.from(files).map((file) => ({
          file,
          preview: URL.createObjectURL(file),
        })),
      ];
      setOrderItems(updatedOrderItems);
    };

    const handleRemoveTrackingFile = (
      orderItemIndex,
      shipmentIndex,
      file,
      isNew = false
    ) => {
      const updatedOrderItems = [...orderItems];
      if (!updatedOrderItems[orderItemIndex]) {
        console.error(`Order item at index ${orderItemIndex} does not exist.`);
        return;
      }

      if (!updatedOrderItems[orderItemIndex].shipments) {
        console.error(
          `Shipments array for order item at index ${orderItemIndex} does not exist.`
        );
        return;
      }

      if (!updatedOrderItems[orderItemIndex].shipments[shipmentIndex]) {
        console.error(
          `Shipment at index ${shipmentIndex} does not exist for order item ${orderItemIndex}.`
        );
        return;
      }

      const shipment =
        updatedOrderItems[orderItemIndex].shipments[shipmentIndex];

      if (isNew) {
        shipment.newFiles = shipment.newFiles?.filter((f) => f !== file) || [];
        setOrderItems(updatedOrderItems);
      } else {
        MySwal.fire({
          title: "Are you sure?",
          text: "This action will permanently delete the tracking file from the database.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete it!",
          cancelButtonText: "Cancel",
        }).then(async (result) => {
          if (result.isConfirmed) {
            try {
              await deleteShipmentTrackingFile(file.id);
              shipment.existingFiles =
                shipment.existingFiles?.filter((f) => f.id !== file.id) || [];

              shipment.removedFiles = [...(shipment.removedFiles || []), file];

              setOrderItems(updatedOrderItems);

              MySwal.fire(
                "Deleted!",
                "The tracking file has been deleted.",
                "success"
              );
            } catch (error) {
              console.error("Error deleting tracking file:", error);
              MySwal.fire(
                "Error",
                "Failed to delete the tracking file.",
                "error"
              );
            }
          }
        });
      }
    };

    const handleTrackingFilePreview = (url) => {
      window.open(url, "_blank");
    };

    const fetchData = async () => {
      try {
        const creationStatusResponse = await getAllStatus();
        setCreationStatus(creationStatusResponse.data);

        const brandResponse = await fetchBrandsBySelectedClients([clientId]);
        setBrands(brandResponse.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    useEffect(() => {
      fetchData();
    }, [clientId]);

    const fetchProducts = async (brand_id) => {
      const productResponse = await getProductsByBrand(brand_id);
      setProducts(productResponse.data);
    };

    const [order, setOrder] = useState({});
    const fetchOrderDetails = async () => {
      if (orderId) {
        try {
          const orderDetails = (await getProductOrderById(orderId)).data;
          orderDetails.order_items.forEach((item, index) => {
            handleProductChange(
              item.product_variation_detail.product_id,
              index
            );
          });
          console.log("dddd", orderDetails);

          const mappedOrderItems = orderDetails.order_items.map((item) => ({
            id: item.id,
            product: item.product_variation_detail.product_id,
            productVariation: item.product_variation,
            warehouse: item.warehouse,
            warehouseDetail: item.warehouse_detail,
            warehouseName: item.warehouse_detail?.name || "", 
            productVariationDesc: item.product_variation_detail?.product_variation_desc || "", 
            quantity: item.quantity,
            unitPrice: item.unit_price,
            deliveryCost: item.delivery_cost || 0,
            labelCost: item.label_cost || 0,
            total: (item.quantity * item.unit_price).toFixed(3),
            finalUnitPrice: (
              (item.quantity * item.unit_price +
                item.delivery_cost +
                item.label_cost) /
              item.quantity
            ).toFixed(3),
            shipments: item.shipments.map((shipment) => ({
              id: shipment.id,
              shipmentDate: shipment.shipment_date,
              quantity: shipment.quantity,
              trackingNumber: shipment.tracking_number,
              existingFiles: shipment.tracking_files || [],
              newFiles: [],
            })),
            receivings: item.receivings || [],
            delivered: item.delivered || 0,
            pending: item.pending || 0,
          }));

          const orderSummaryList = orderDetails.order_items.map((item) => ({
            productVariation: item.product_variation, 
            productVariationDesc: item.product_variation_detail.product_variation_desc,
            warehouse: item.warehouse,                
            warehouseName: item.warehouse_detail.name,
            quantity: item.quantity,
            delivered: item.delivered,
            finalUnitPrice: (
              (item.quantity * item.unit_price +
                item.delivery_cost +
                item.label_cost) /
              item.quantity
            ).toFixed(3),
          }));
          setOrderSummary(orderSummaryList);
          setOrder(orderDetails);
          setOrderItems(mappedOrderItems);

          orderForm.setFieldsValue({
            invoiceDate: orderDetails.invoice_date,
            invoiceNumber: orderDetails.invoice_number,
            supplier: orderDetails.supplier,
            creationStatus: orderDetails.status,
            brand: orderDetails.brand,
            statusDescription: orderDetails.status_description,
          });
          handleBrandChange(orderDetails.brand);
          setExistingFiles(orderDetails.order_files || []);
        } catch (error) {
          console.error("Error fetching order details:", error);
        }
      }
    };

    useEffect(() => {
      fetchOrderDetails();
    }, [orderId, isVisible]);

    const handleProductChange = async (value, index = 0) => {
      try {
        if (!value) return;
        const variationResponse = await getProductVariationsByProductId(value);
        setProductVariationsList((prevList) => {
          const updatedList = [...prevList];
          updatedList[index] = variationResponse.data;
          return updatedList;
        });
        orderForm.setFieldsValue({ productVariation: null });
      } catch (error) {
        console.error("Error fetching product variations:", error);
      }
    };

    const handleRemoveFile = (file, isNew = false) => {
      if (isNew) {
        setNewFiles((prevFiles) => prevFiles.filter((f) => f !== file));
      } else {
        MySwal.fire({
          title: "Are you sure?",
          text: "This action will permanently delete the file from the database.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete it!",
          cancelButtonText: "Cancel",
        }).then(async (result) => {
          if (result.isConfirmed) {
            try {
              await deleteOrderFile(file.id);
              setRemovedFiles((prevFiles) => [...prevFiles, file]);
              setExistingFiles((prevFiles) =>
                prevFiles.filter((f) => f.id !== file.id)
              );

              MySwal.fire("Deleted!", "The file has been deleted.", "success");
            } catch (error) {
              console.error("Error deleting file:", error);
              MySwal.fire("Error", "Failed to delete the file.", "error");
            }
          }
        });
      }
    };

    const handlePreviewClick = (url) => {
      window.open(url, "_blank");
    };

    useEffect(() => {
      const sidebarWidth = isFullScreen ? window.innerWidth * 0.6 : 555;
      onResize(sidebarWidth);
    }, [isFullScreen, onResize]);

    const resetState = () => {
      orderForm.resetFields();
      orderItemForm.resetFields();
      setProducts([]);
      setProductVariationsList([]);
      setSuppliers([]);
      setWarehouses([]);
      setStatuses([]);
      setExistingFiles([]);
      setNewFiles([]);
      setRemovedFiles([]);
      setIsFullScreen(false);
      setOrderSummary([])
      setOrderItems([{
        productVariation: "",
        warehouse: "",
        quantity: 0,
        unitPrice: 0,
        shipments: [],
        receivings: [],
      }] )
      
    };

    const handleSubmitOrder = async () => {
      try {
        const values = await orderForm.validateFields();
        const orderPayload = {
          invoice_date: values.invoiceDate,
          invoice_number: values.invoiceNumber,
          supplier: values.supplier,
          status: values.creationStatus || 1,
          status_description: values.statusDescription,
          brand: values.brand,
        };

        let orderResponse;

        let updatedOrderId;
        if (orderId) {
          orderResponse = await updateProductOrder(orderId, orderPayload);
          updatedOrderId = orderId;
        } else {
          orderResponse = await createProductOrder(orderPayload);
          updatedOrderId = orderResponse.data.id;
          setOrderId(updatedOrderId);
        }
    
        
        for (const item of orderItems) {
          const itemPayload = {
            product_variation: item.productVariation,
            warehouse: item.warehouse,
            quantity: item.quantity,
            unit_price: item.unitPrice || 0,
            delivery_cost: item.deliveryCost || 0,
            label_cost: item.labelCost || 0,
            product_order: updatedOrderId,
          };
        
          if (item.id) {
            await updateOrderItem(item.id, itemPayload); 
          } else {
            const created = await createOrderItem(itemPayload); 
            item.id = created.data.id;
          }
        }
        
    
        // 3. Upload invoice files
        for (const fileObj of newFiles) {
          await uploadOrderFile(updatedOrderId, fileObj.file);
        }
    
        await fetchOrderDetails();
        fetchOrderList();
    
        Swal.fire({
          title: "Success",
          text: orderId ? "Order updated successfully." : "Order created successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
    
      } catch (error) {
        console.error("Order submission error:", error);
        Swal.fire({
          title: "Error",
          text: "An error occurred while submitting the order.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };
    
    const handleShipmentSubmit = async (
      orderItemId,
      orderItemIndex,
      shipmentIndex
    ) => {
      try {
        const values = await orderItemForm.validateFields();
        const shipment = orderItems[orderItemIndex].shipments[shipmentIndex];

        if (!shipment.newFiles) {
          shipment.newFiles = [];
        }

        const shipmentPayload = {
          order: orderItemId,
          shipment_date:
            values[`shipmentDate_${orderItemIndex}_${shipmentIndex}`],
          quantity:
            values[`shipment_quantity_${orderItemIndex}_${shipmentIndex}`],
          tracking_number:
            values[`trackingNumber_${orderItemIndex}_${shipmentIndex}`],
        };

        let shipmentResponse;
        if (shipment.id) {
          shipmentResponse = await updateShipment(shipment.id, shipmentPayload);
        } else {
          shipmentResponse = await createShipment(shipmentPayload);
        }

        const shipmentId = shipmentResponse.data.id;

        // Upload new tracking files
        for (const file of shipment.newFiles) {
          const formData = new FormData();
          formData.append("shipment", shipmentId);
          formData.append("file", file.file);
          await uploadShipmentTrackingFile(shipmentId, file.file);
        }

        fetchOrderDetails();

        Swal.fire({
          title: "Success",
          text: "Shipment saved successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } catch (error) {
        console.error("Error submitting shipment:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to save shipment. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };

    const handleReceivingSubmit = async (
      orderId,
      orderItemIndex,
      receivingIndex
    ) => {
      try {
        const values = await orderItemForm.validateFields();
        const updatedOrderItems = [...orderItems];
        if (!updatedOrderItems[orderItemIndex]) {
          console.error(
            `Order item at index ${orderItemIndex} does not exist.`
          );
          return;
        }
        if (!updatedOrderItems[orderItemIndex].receivings) {
          console.error(
            `Receivings array for order item at index ${orderItemIndex} does not exist.`
          );
          return;
        }
        if (!updatedOrderItems[orderItemIndex].receivings[receivingIndex]) {
          console.error(
            `Receiving at index ${receivingIndex} does not exist for order item ${orderItemIndex}.`
          );
          return;
        }

        const receiving =
          updatedOrderItems[orderItemIndex].receivings[receivingIndex];
        receiving.deliveryDate =
          values[`deliveryDate_${orderItemIndex}_${receivingIndex}`];
        receiving.quantity =
          values[`receiving_quantity_${orderItemIndex}_${receivingIndex}`];

        const receivingPayload = {
          order: orderId,
          delivery_date: receiving.deliveryDate,
          quantity: receiving.quantity,
        };

        if (receiving.id) {
          await updateReceiving(receiving.id, receivingPayload);
        } else {
          const newReceiving = await createReceiving(receivingPayload);
        }
        fetchOrderDetails();

        Swal.fire({
          title: "Success",
          text: "Receiving submitted successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } catch (error) {
        console.error("Error submitting receiving:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to submit receiving. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };

    const calculateFinalUnitPrice = (qty, total, deliveryCost, labelCost) =>
      (parseFloat(total || 0) +
        parseFloat(deliveryCost || 0) +
        parseFloat(labelCost || 0)) /
      qty;

    const removeShipment = async (orderIndex, shipmentIndex) => {
      MySwal.fire({
        title: "Are you sure?",
        text: "This action will remove the shipment and its associated files permanently.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, remove it!",
        cancelButtonText: "Cancel",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const updatedOrderItems = [...orderItems];
            const shipmentToRemove =
              updatedOrderItems[orderIndex].shipments[shipmentIndex];

            // If the shipment exists in the database
            if (shipmentToRemove.id) {
              for (const file of shipmentToRemove.existingFiles) {
                await deleteShipmentTrackingFile(file.id);
              }
              await deleteShipment(shipmentToRemove.id);
            }

            // Remove the shipment from the state
            updatedOrderItems[orderIndex].shipments.splice(shipmentIndex, 1);
            setOrderItems(updatedOrderItems);

            MySwal.fire(
              "Removed!",
              "The shipment has been removed.",
              "success"
            );
          } catch (error) {
            console.error("Error removing shipment:", error);
            MySwal.fire("Error", "Failed to remove the shipment.", "error");
          }
        }
      });
    };

    const removeReceiving = async (orderIndex, receivingIndex) => {
      MySwal.fire({
        title: "Are you sure?",
        text: "This action will remove the receiving permanently.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, remove it!",
        cancelButtonText: "Cancel",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const updatedOrderItems = [...orderItems];
            const receivingToRemove =
              updatedOrderItems[orderIndex].receivings[receivingIndex];

            if (receivingToRemove.id) {
              await deleteReceiving(receivingToRemove.id, null);
            }

            updatedOrderItems[orderIndex].receivings.splice(receivingIndex, 1);
            setOrderItems(updatedOrderItems);

            MySwal.fire(
              "Removed!",
              "The receiving has been removed.",
              "success"
            );
          } catch (error) {
            console.error("Error removing receiving:", error);
            MySwal.fire("Error", "Failed to remove the receiving.", "error");
          }
        }
      });
    };

    const handleSaveProductInfo = async (index) => {
      try {
        const values = await orderItemForm.validateFields();
        const item = orderItems[index];
        const productVariationObj = productVariationsList[index]?.find(
          (v) => v.id === item.productVariation
        );
        const warehouseObj = warehouses.find((w) => w.id === item.warehouse);
        const updatedItem = {
          ...item,
          productVariationDesc: productVariationObj?.product_variation_desc || "",
          warehouseName: warehouseObj?.name || "",
        };
        updateOrderItemFunction(index, updatedItem);
        setOrderSummary((prev) => {
            return [...prev, updatedItem];
        });
    
      } catch (error) {
        console.error("Error saving product information:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to save product information. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };
    const handleBrandChange = async (value) => {
      if (value) {
        const supplierResponse = await getSuppliersByType(
          "Product Supplier",
          value
        );
        setSuppliers(supplierResponse.data || []);

        const warehouseResponse = await getWarehouseAddresses(value);
        setWarehouses(warehouseResponse.data);
        fetchProducts(value);
      }
    };

    return (
      <div
        ref={ref}
        className={`right-sidebar ${isVisible ? "visible" : ""}`}
        style={{
          position: "fixed",
          top: data ? 0 : 66,
          right: 0,
          height: "100%",
          zIndex: 1050,
          width: isFullScreen ? "60%" : "555px",
          backgroundColor: "#fff",
          boxShadow: "0 0 3px rgba(0, 0, 0, 0.6)",
          overflowY: "auto",
          overflowX: "hidden",
          transition: "transform 0.3s ease-in-out",
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
        }}
      >
        {/* Sidebar Header */}
        <div
          className="sidebar-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "15px",
            borderBottom: "1px solid #ddd",
          }}
          id="top"
        >
          <h5>{orderId ? "Edit Product Order" : "Create New Product Order"}</h5>
          <div>
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="btn"
            >
              <FeatherIcon icon={isFullScreen ? "minimize" : "maximize"} />
            </button>
            <button
              className="btn"
              onClick={() => {
                resetState();
                onClose();
              }}
            >
              <X />
            </button>
          </div>
        </div>

        <div style={{ padding: "10px" }}>
          <Form form={orderForm} layout="vertical">
            <Card title="Order Creation Status">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <Form.Item
                  label="Creation Status"
                  name="creationStatus"
                  rules={[
                    { required: true, message: "Please select a status" },
                  ]}
                >
                  <Select placeholder="Select Status">
                    {creationStatus?.map((status) => (
                      <Option key={status.id} value={status.id}>
                        {status.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item label="Status Description" name="statusDescription">
                  <Input.TextArea
                    rows={2}
                    placeholder="Enter status description"
                    allowClear
                  />
                </Form.Item>
              </div>
            </Card>
            <br />
            <Card title="Order Completion Status" hidden>
              <Form.Item label="Status" name="status" initialValue={1}>
                <Select placeholder="Select Status" disabled>
                  {statuses.map((status) => (
                    <Option key={status.id} value={status.id}>
                      {status.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <Form.Item label="Delivered" name="delivered">
                  <Input type="number" disabled />
                </Form.Item>
                <Form.Item label="Pending" name="pending">
                  <Input type="number" disabled />
                </Form.Item>
              </div>
            </Card>

            <br />
            <Card title="Invoice">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: "10px",
                }}
              >
                <Form.Item
                  label="Invoice Date"
                  name="invoiceDate"
                  initialValue={new Date().toISOString().split("T")[0]}
                  rules={[
                    {
                      required: true,
                      message: "Please enter the invoice date",
                    },
                  ]}
                >
                  <Input type="date" />
                </Form.Item>
                <Form.Item
                  label="Invoice Number"
                  name="invoiceNumber"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the invoice number",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Brand"
                  name="brand"
                  rules={[{ required: true, message: "Please select a brand" }]}
                >
                  <Select
                    placeholder="Select Brand"
                    allowClear
                    disabled={orderId}
                    loading={!brands.length}
                    onChange={handleBrandChange}
                  >
                    {brands.map((brand) => (
                      <Option key={brand.id} value={brand.id}>
                        {brand.brand_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Supplier"
                  name="supplier"
                  rules={[
                    { required: true, message: "Please select a supplier" },
                  ]}
                >
                  <Select
                    placeholder="Select Supplier"
                    allowClear
                    loading={!suppliers.length}
                  >
                    {suppliers.map((supplier) => (
                      <Option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            </Card>
            <br />
          </Form>

          <Form form={orderItemForm} layout="vertical">
            <Collapse accordion>
              {orderItems.map((item, index) => (
                <Collapse.Panel
                  header={`Product Information ${index + 1}`}
                  key={index}
                >
                  <div
                    className="row"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "5px",
                      overflowX: "scroll",
                    }}
                  >
                    <Form.Item
                      label="Product"
                      name={`product_${index}`}
                      initialValue={item.product || ""}
                      rules={[
                        { required: true, message: "Please select a product" },
                      ]}
                    >
                      <Select
                        placeholder="Select Product"
                        onChange={(value) => {
                          handleProductChange(value, index);
                          updateOrderItemFunction(index, {
                            ...item,
                            product: value,
                          });
                        }}
                        allowClear
                      >
                        {products.map((product) => (
                          <Option key={product.id} value={product.id}>
                            {product.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label="Product Variation"
                      name={`productVariation_${index}`}
                      initialValue={item.productVariation || ""}
                      rules={[
                        {
                          required: true,
                          message: "Please select a product variation",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Product Variation"
                        onChange={(value) =>
                          updateOrderItemFunction(index, {
                            ...item,
                            productVariation: value,
                          })
                        }
                        allowClear
                      >
                        {productVariationsList[index]?.map((variation) => (
                          <Option key={variation.id} value={variation.id}>
                            {variation.product_variation_desc}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label="Warehouse"
                      name={`warehouse_${index}`}
                      initialValue={item.warehouse || ""}
                      rules={[
                        {
                          required: true,
                          message: "Please select a warehouse",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Warehouse"
                        onChange={(value) =>
                          updateOrderItemFunction(index, {
                            ...item,
                            warehouse: value,
                          })
                        }
                        allowClear
                      >
                        {warehouses.map((warehouse) => (
                          <Option key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      label="Quantity of Order"
                      name={`quantity_${index}`}
                      initialValue={item.quantity || ""}
                      rules={[
                        { required: true, message: "Please enter quantity" },
                      ]}
                    >
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const updatedQuantity =
                            parseFloat(e.target.value) || 0;
                          const updatedTotal = updatedQuantity * item.unitPrice;
                          const updatedFinalUnitPrice =
                            updatedQuantity > 0
                              ? (updatedTotal +
                                  (item.deliveryCost || 0) +
                                  (item.labelCost || 0)) /
                                updatedQuantity
                              : 0;

                          updateOrderItemFunction(index, {
                            ...item,
                            quantity: updatedQuantity,
                            total: updatedTotal.toFixed(2),
                            finalUnitPrice: updatedFinalUnitPrice.toFixed(2),
                          });
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      label="Unit Price"
                      name={`unitPrice_${index}`}
                      initialValue={item.unitPrice || 0}
                    >
                      <Input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => {
                          const updatedUnitPrice =
                            parseFloat(e.target.value) || 0;
                          const updatedTotal = item.quantity * updatedUnitPrice;
                          const updatedFinalUnitPrice =
                            item.quantity > 0
                              ? (updatedTotal +
                                  (item.deliveryCost || 0) +
                                  (item.labelCost || 0)) /
                                item.quantity
                              : 0;

                          updateOrderItemFunction(index, {
                            ...item,
                            unitPrice: updatedUnitPrice,
                            total: updatedTotal.toFixed(2),
                            finalUnitPrice: updatedFinalUnitPrice.toFixed(2),
                          });
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      label="Total"
                      initialValue={item.total}
                      name={`total_${index}`}
                    >
                      <Input value={item.total} disabled />
                    </Form.Item>

                    <Form.Item
                      label="Delivery Cost"
                      name={`deliveryCost_${index}`}
                      initialValue={item.deliveryCost || 0}
                    >
                      <Input
                        type="number"
                        onChange={(e) => {
                          const updatedDeliveryCost = parseFloat(
                            e.target.value
                          );
                          const updatedFinalUnitPrice = calculateFinalUnitPrice(
                            item.quantity,
                            item.total,
                            updatedDeliveryCost,
                            item.labelCost
                          );

                          updateOrderItemFunction(index, {
                            ...item,
                            deliveryCost: updatedDeliveryCost,
                            finalUnitPrice: updatedFinalUnitPrice,
                          });
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      label="Label Cost"
                      name={`labelCost_${index}`}
                      initialValue={item.labelCost || 0}
                    >
                      <Input
                        type="number"
                        onChange={(e) => {
                          const updatedLabelCost = parseFloat(e.target.value);
                          const updatedFinalUnitPrice = calculateFinalUnitPrice(
                            item.quantity,
                            item.total,
                            item.deliveryCost,
                            updatedLabelCost
                          );

                          updateOrderItemFunction(index, {
                            ...item,
                            labelCost: updatedLabelCost,
                            finalUnitPrice: updatedFinalUnitPrice,
                          });
                        }}
                      />
                    </Form.Item>

                    <Form.Item
                      label="Final Unit Price"
                      name={`finalUnitPrice_${index}`}
                      initialValue={item.finalUnitPrice || 0}
                    >
                      <Input value={item.finalUnitPrice} disabled />
                    </Form.Item>
                  </div>

                  <br />
                  <div hidden={!item.id}>
                    <Collapse>
                      <Collapse.Panel
                        header="Shipment Details"
                        key={`shipments_${index}`}
                      >
                        <Collapse>
                          {item.shipments?.map((shipment, shipmentIndex) => (
                            <Collapse.Panel
                              header={`Shipment ${shipmentIndex + 1}`}
                              key={`shipment_${shipmentIndex}`}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr 1fr",
                                  gap: "10px",
                                }}
                              >
                                <Form.Item
                                  label="Shipment Date"
                                  name={`shipmentDate_${index}_${shipmentIndex}`}
                                  initialValue={
                                    shipment.shipmentDate ||
                                    new Date().toISOString().split("T")[0]
                                  }
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please enter the shipment date",
                                    },
                                  ]}
                                >
                                  <Input
                                    type="date"
                                    onChange={(e) =>
                                      updateShipmentFunction(
                                        index,
                                        shipmentIndex,
                                        {
                                          ...shipment,
                                          shipmentDate: e.target.value,
                                        }
                                      )
                                    }
                                  />
                                </Form.Item>
                                <Form.Item
                                  label="Quantity"
                                  name={`shipment_quantity_${index}_${shipmentIndex}`}
                                  initialValue={shipment.quantity}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please enter the quantity",
                                    },
                                  ]}
                                >
                                  <Input
                                    type="number"
                                    onChange={(e) =>
                                      updateShipmentFunction(
                                        index,
                                        shipmentIndex,
                                        {
                                          ...shipment,
                                          quantity: parseInt(
                                            e.target.value,
                                            10
                                          ),
                                        }
                                      )
                                    }
                                  />
                                </Form.Item>
                                <Form.Item
                                  label="Tracking Number"
                                  name={`trackingNumber_${index}_${shipmentIndex}`}
                                  initialValue={shipment.trackingNumber}
                                  rules={[
                                    {
                                      required: true,
                                      message:
                                        "Please enter the tracking number",
                                    },
                                  ]}
                                >
                                  <Input
                                    onChange={(e) =>
                                      updateShipmentFunction(
                                        index,
                                        shipmentIndex,
                                        {
                                          ...shipment,
                                          trackingNumber: e.target.value,
                                        }
                                      )
                                    }
                                  />
                                </Form.Item>
                              </div>

                              <div className="text-editor add-list add">
                                <div className="col-lg-12">
                                  <div className="add-choosen">
                                    <div className="input-blocks">
                                      <div className="image-upload">
                                        <input
                                          type="file"
                                          multiple
                                          onChange={(event) => {
                                            handleTrackingFileUpload(
                                              index,
                                              shipmentIndex,
                                              event.target.files
                                            );
                                            event.target.value = "";
                                          }}
                                          accept="application/pdf"
                                        />
                                        <div className="image-uploads">
                                          <PlusCircle className="plus-down-add me-0" />
                                          <h4>Upload Tracking Files</h4>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Existing Files */}
                                    {shipment.existingFiles?.length > 0 && (
                                      <div className="existing-files mt-3">
                                        <h6>Existing Files</h6>
                                        <div className="d-flex flex-wrap">
                                          {shipment.existingFiles.map(
                                            (file) => (
                                              <div
                                                key={file.id}
                                                className="phone-img"
                                              >
                                                <button
                                                  className="file-thumbnail"
                                                  type="button"
                                                  onClick={(event) => {
                                                    event.preventDefault();
                                                    handleTrackingFilePreview(
                                                      file.signed_file_url
                                                    );
                                                  }}
                                                  style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    cursor: "pointer",
                                                  }}
                                                >
                                                  <embed
                                                    src={file.signed_file_url}
                                                    type="application/pdf"
                                                    className="file-thumbnail"
                                                    width="100%"
                                                    height="200"
                                                  />
                                                </button>
                                                <Link to="#">
                                                  <X
                                                    className="x-square-add remove-product"
                                                    onClick={() =>
                                                      handleRemoveTrackingFile(
                                                        index,
                                                        shipmentIndex,
                                                        file,
                                                        false
                                                      )
                                                    }
                                                  />
                                                </Link>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* New Files */}
                                    {shipment.newFiles?.length > 0 && (
                                      <div className="new-files mt-3">
                                        <h6>New Files</h6>
                                        <div className="d-flex flex-wrap">
                                          {shipment.newFiles.map(
                                            (fileObj, fileIndex) => (
                                              <div
                                                key={fileIndex}
                                                className="phone-img"
                                              >
                                                <button
                                                  className="file-thumbnail"
                                                  type="button"
                                                  onClick={(event) => {
                                                    event.preventDefault();
                                                    handleTrackingFilePreview(
                                                      fileObj.preview
                                                    );
                                                  }}
                                                >
                                                  <embed
                                                    src={fileObj.preview}
                                                    type="application/pdf"
                                                    className="file-thumbnail"
                                                    width="100%"
                                                    height="200"
                                                  />
                                                </button>
                                                <Link to="#">
                                                  <X
                                                    className="x-square-add remove-product"
                                                    onClick={() => {
                                                      URL.revokeObjectURL(
                                                        fileObj.preview
                                                      ); // Cleanup URL
                                                      handleRemoveTrackingFile(
                                                        index,
                                                        fileObj,
                                                        true
                                                      );
                                                    }}
                                                  />
                                                </Link>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="d-flex justify-content-end mt-3">
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm me-2"
                                  onClick={() => {
                                    removeShipment(index, shipmentIndex);
                                  }}
                                >
                                  Remove
                                </button>
                                <button
                                  type="submit"
                                  className="btn btn-submit btn-sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleShipmentSubmit(
                                      item.id,
                                      index,
                                      shipmentIndex
                                    );
                                  }}
                                >
                                  {shipment.id
                                    ? "Update Shipment"
                                    : "Save Shipment"}
                                </button>
                              </div>
                            </Collapse.Panel>
                          ))}
                        </Collapse>
                        <Button
                          type="dashed"
                          onClick={() => addShipmentToOrderItem(index)}
                          className="mt-2"
                          icon={<FeatherIcon icon="plus" />}
                        >
                          Add Shipment
                        </Button>
                      </Collapse.Panel>

                      <Collapse.Panel
                        header="Receiving Details"
                        key={`receivings_${index}`}
                      >
                        <Collapse>
                          {item.receivings?.map((receiving, receivingIndex) => (
                            <Collapse.Panel
                              header={`Receiving ${receivingIndex + 1}`}
                              key={`receiving_${receivingIndex}`}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "10px",
                                }}
                              >
                                <Form.Item
                                  label="Delivery Date"
                                  name={`deliveryDate_${index}_${receivingIndex}`}
                                  initialValue={
                                    receiving.deliveryDate ||
                                    new Date().toISOString().split("T")[0]
                                  }
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please enter the delivery date",
                                    },
                                  ]}
                                >
                                  <Input
                                    type="date"
                                    onChange={(e) =>
                                      updateReceivingFunction(
                                        index,
                                        receivingIndex,
                                        {
                                          ...receiving,
                                          deliveryDate: e.target.value,
                                        }
                                      )
                                    }
                                  />
                                </Form.Item>
                                <Form.Item
                                  label="Quantity"
                                  name={`receiving_quantity_${index}_${receivingIndex}`}
                                  initialValue={receiving.quantity}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please enter the quantity",
                                    },
                                  ]}
                                >
                                  <Input
                                    type="number"
                                    onChange={(e) =>
                                      updateReceivingFunction(
                                        index,
                                        receivingIndex,
                                        {
                                          ...receiving,
                                          quantity: parseInt(
                                            e.target.value,
                                            10
                                          ),
                                        }
                                      )
                                    }
                                  />
                                </Form.Item>
                              </div>
                              <div className="d-flex justify-content-end">
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm me-2"
                                  onClick={() => {
                                    removeReceiving(index, receivingIndex);
                                  }}
                                >
                                  Remove
                                </button>
                                <button
                                  type="submit"
                                  className="btn btn-submit btn-sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleReceivingSubmit(
                                      item.id,
                                      index,
                                      receivingIndex
                                    );
                                  }}
                                >
                                  {receiving.id
                                    ? "Update Receiving"
                                    : "Save Receiving"}
                                </button>
                              </div>
                            </Collapse.Panel>
                          ))}
                        </Collapse>
                        <Button
                          type="dashed"
                          onClick={() => addReceivingToOrderItem(index)}
                          className="mt-2"
                          icon={<FeatherIcon icon="plus" />}
                        >
                          Add Receiving
                        </Button>
                      </Collapse.Panel>
                    </Collapse>
                  </div>

                  <br />
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-danger btn-sm me-2"
                      onClick={(e) => {
                        e.preventDefault();
                        deleteOrderItemFunction(index);
                      }}
                    >
                      Remove
                    </button>
                    <button
                      type="button"
                      className="btn btn-submit"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSaveProductInfo(index);
                      }}
                    >
                      Add to Order Summary
                    </button>
                  </div>
                </Collapse.Panel>
              ))}
            </Collapse>

            <Button
              type="dashed"
              onClick={addOrderItem}
              className="mt-2"
              icon={<PlusCircle size={18} />}
              block
            >
              Add Product
            </Button>
            <br />
            <br />
          </Form>

          <Card title="Invoice Files">
            <div className="text-editor add-list add">
              <div className="col-lg-12">
                <div className="add-choosen">
                  <div className="input-blocks">
                    <div className="image-upload">
                      <input
                        type="file"
                        multiple
                        onChange={(event) => {
                          const input = event.target;
                          const files = Array.from(input.files);

                          setNewFiles((prevFiles) => [
                            ...prevFiles,
                            ...files.map((file) => ({
                              file,
                              preview: URL.createObjectURL(file),
                            })),
                          ]);
                          input.value = "";
                        }}
                        accept="application/pdf"
                      />
                      <div className="image-uploads">
                        <PlusCircle className="plus-down-add me-0" />
                        <h4>Upload Invoice Files</h4>
                      </div>
                    </div>
                  </div>

                  {/* Existing Files */}
                  {existingFiles?.length > 0 && (
                    <div className="existing-files mt-3">
                      <h6>Existing Files</h6>
                      <div className="d-flex flex-wrap">
                        {existingFiles.map((file) => (
                          <div key={file.id} className="phone-img">
                            <button
                              className="file-thumbnail"
                              type="button"
                              onClick={(event) => {
                                event.preventDefault();
                                handlePreviewClick(file.signed_file_url);
                              }}
                              style={{
                                width: "100%",
                                height: "100%",
                                cursor: "pointer",
                              }}
                            >
                              <embed
                                src={file.signed_file_url}
                                type="application/pdf"
                                className="file-thumbnail"
                                width="100%"
                                height="200"
                              />
                            </button>
                            <Link to="#">
                              <X
                                className="x-square-add remove-product"
                                onClick={() => handleRemoveFile(file)}
                              />
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Files */}
                  {newFiles?.length > 0 && (
                    <div className="new-files mt-3">
                      <h6>New Files</h6>
                      <div className="d-flex flex-wrap">
                        {newFiles.map((fileObj, index) => (
                          <div key={index} className="phone-img">
                            <button
                              className="file-thumbnail"
                              type="button"
                              onClick={(event) => {
                                event.preventDefault();
                                handlePreviewClick(fileObj.preview);
                              }}
                            >
                              <embed
                                src={fileObj.preview}
                                type="application/pdf"
                                className="file-thumbnail"
                                width="100%"
                                height="200"
                              />
                              <div className="file-name">
                                {fileObj.file.name}
                              </div>
                            </button>
                            <Link to="#">
                              <X
                                className="x-square-add remove-product"
                                onClick={() => {
                                  URL.revokeObjectURL(fileObj.preview); // Cleanup URL
                                  setNewFiles((prevFiles) =>
                                    prevFiles.filter((f) => f !== fileObj)
                                  );
                                }}
                              />
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
          <Card title="Order Summary" className="mt-4">
            <div style={{ overflowX: "scroll" }}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Product Variation</th>
                    <th>Warehouse</th>
                    <th>Qty Order</th>
                    <th>Final Unit Price</th>
                    <th>Delivered</th>
                  </tr>
                </thead>
                <tbody>
                  {orderSummary.map((item, index) => (
                    <tr key={index}>
                      <td>{item.productVariationDesc}</td>
                      <td>{item.warehouseName}</td>
                      <td>{item.quantity}</td>
                      <td>${item.finalUnitPrice}</td>
                      <td>{item.delivered}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <br />
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-cancel me-2"
              onClick={() => {
                onClose();
                resetState();
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-submit"
              onClick={(e) => {
                e.preventDefault();
                handleSubmitOrder();
              }}
            >
              {orderId ? "Update Order" : "Create Order"}
            </button>
          </div>
          <br />
          <br />
          <div style={{ height: "100px" }}></div>
        </div>
      </div>
    );
  }
);

OrderDetailsView.displayName = "OrderDetailsView";

export default OrderDetailsView;
